import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../../web/components-v2/Theme/theme';
import { shadowSm } from '../../../../web/styles/shadows';

const { fontFamilies, fontSizes, fontWeights } = THEME.typography;

export const ROW_COLUMNS = '200px 180px minmax(0, 1fr) 110px 190px 120px';

const clickable = {
	position: 'relative' as const,
	transition: 'border-color 120ms ease-out, box-shadow 120ms ease-out',
	':hover': {
		borderColor: THEME.colors.border.main,
		boxShadow: shadowSm,
	},
};

export const styleSheet = StyleSheet.create({
	dueStacked: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
	},
	dueInline: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[8],
	},
	dueDate: {
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.bold,
	},
	dueIcons: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[4],
	},
	iconWrap: {
		display: 'inline-flex',
	},
	icon16: {
		height: 16,
		width: 16,
	},
	icon18: {
		height: 18,
		width: 18,
	},
	muted: {
		color: THEME.colors.text.light,
		fontSize: fontSizes.sm,
	},
	mutedSmall: {
		color: THEME.colors.text.light,
		fontSize: fontSizes.xs,
	},
	small: {
		fontSize: fontSizes.xs,
	},
	recording: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[4],
	},
	section: {
		display: 'flex',
		flexDirection: 'column',
	},
	sectionTrigger: {
		color: THEME.colors.text.main,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: `${SPACE[8]}px ${SPACE[4]}px`,
		width: '100%',
	},
	sectionChevron: {
		color: THEME.colors.icon.gray,
		flexShrink: 0,
		transition: 'transform 150ms ease-out',
	},
	sectionChevronClosed: {
		transform: 'rotate(-90deg)',
	},
	sectionLabel: {
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.lg,
		fontWeight: fontWeights.bold,
	},
	sectionLabelOverdue: {
		color: THEME.colors.danger.dark,
	},
	row: {
		...clickable,
		alignItems: 'center',
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		fontSize: fontSizes.sm,
		padding: `${SPACE[12]}px ${SPACE[16]}px`,
	},
	rowGrid: {
		columnGap: `${SPACE[16]}px`,
		display: 'grid',
		gridTemplateColumns: ROW_COLUMNS,
		rowGap: SPACE[8],
		[THEME.queries.lg]: {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
	},
	company: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		minWidth: 0,
	},
	companyName: {
		fontWeight: fontWeights.bold,
	},
	openLead: {
		color: THEME.colors.text.main,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: 0,
		textAlign: 'left',
		whiteSpace: 'normal',
		':hover': {
			color: THEME.colors.text.main,
			textDecoration: 'none',
		},
		':after': {
			borderRadius: THEME.common.borderRadius,
			content: '""',
			inset: 0,
			position: 'absolute',
		},
		':focus-visible': {
			outline: 'none',
		},
		':focus-visible::after': {
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	rowActions: {
		justifySelf: 'start',
	},
	aboveOpenLead: {
		position: 'relative',
		zIndex: 1,
	},
	note: {
		color: THEME.colors.text.main,
	},
	card: {
		...clickable,
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxShadow: shadowSm,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
		padding: SPACE[20],
	},
	cardHeader: {
		alignItems: 'flex-start',
		display: 'flex',
		gap: SPACE[12],
		justifyContent: 'space-between',
	},
	cardCompanyName: {
		fontFamily: fontFamilies.sora,
		fontSize: fontSizes.md,
		fontWeight: fontWeights.bold,
	},
	contact: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[12],
	},
	contactAvatar: {
		height: 32,
		width: 32,
	},
	contactName: {
		fontSize: fontSizes.sm,
		fontWeight: fontWeights.semibold,
	},
	divider: {
		backgroundColor: THEME.colors.border.light,
		height: 1,
	},
	cardFollowUp: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		minWidth: 0,
	},
	cardNote: {
		color: THEME.colors.text.light,
		fontSize: fontSizes.sm,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	meeting: {
		alignItems: 'center',
		display: 'flex',
		fontSize: fontSizes.sm,
		gap: SPACE[8],
		marginTop: SPACE[4],
	},
	cardFooter: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		gap: SPACE[12],
		justifyContent: 'space-between',
		marginTop: 'auto',
		padding: SPACE[12],
	},
	cardFooterActions: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[12],
	},
});
